import type {
  AppointmentPlanningDateRangeFilter,
  AppointmentPlanningFilterState,
} from "../../../../../../../_logic/appointments-contract"
import type {
  PlanningPreviewRow,
  PlanningPreviewSelectOption,
} from "../../../../../_shared/_logic/table-contract"
import { STABLE_ROW_NUMBER_COLUMN_ID } from "../../../../../../../_logic/stable-row-numbers"
import {
  addDays,
  startOfDay,
  startOfWeek,
} from "../../../../../../../../../../_shared/_logic/date-math"

export type AppointmentPlanningTableSortState = {
  columnId: string
  key: string
  dir: "asc" | "desc"
} | null

export const APPOINTMENT_PLANNING_CALENDAR_MAX_DAYS = 112

export type CompleteAppointmentPlanningDateRange = {
  start: Date
  end: Date
}

export type CalendarDateRangeSelectionResult =
  | { kind: "reset" }
  | { kind: "valid"; range: CompleteAppointmentPlanningDateRange }
  | { kind: "invalid"; message: string }

export const createDefaultAppointmentPlanningFilterState = (): AppointmentPlanningFilterState => ({
  search: "",
  employeeId: "",
  dateRange: {
    start: null,
    end: null,
  },
})

export const normalizeAppointmentPlanningSearch = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLocaleLowerCase("de-DE")

export const appointmentPlanningDateFromValue = (
  value: Date | string | null | undefined,
): Date | null => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value)

  const text = String(value ?? "").trim()

  if (!text) return null

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch
    const parsed = new Date(Number(year), Number(month) - 1, Number(day))

    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  const parsed = new Date(text)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const endOfDay = (value: Date): number => {
  const date = new Date(value)
  date.setHours(23, 59, 59, 999)

  return date.getTime()
}

const localDayNumber = (value: Date): number =>
  Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / 86400000

export const inclusiveAppointmentPlanningDayCount = (start: Date, end: Date): number =>
  localDayNumber(startOfDay(end)) - localDayNumber(startOfDay(start)) + 1

export const normalizeAppointmentPlanningDateRange = (
  value: unknown,
): AppointmentPlanningDateRangeFilter => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>

    return {
      start: record.start instanceof Date || typeof record.start === "string" ? record.start : null,
      end: record.end instanceof Date || typeof record.end === "string" ? record.end : null,
    }
  }

  return {
    start: null,
    end: null,
  }
}

export const completeAppointmentPlanningDateRange = (
  value: AppointmentPlanningDateRangeFilter,
): CompleteAppointmentPlanningDateRange | null => {
  const start = appointmentPlanningDateFromValue(value.start)
  const end = appointmentPlanningDateFromValue(value.end)

  if (!start || !end) return null

  const normalizedStart = startOfDay(start)
  const normalizedEnd = startOfDay(end)

  return normalizedStart <= normalizedEnd
    ? { start: normalizedStart, end: normalizedEnd }
    : { start: normalizedEnd, end: normalizedStart }
}

export const validateAppointmentPlanningCalendarDateRange = (
  value: AppointmentPlanningDateRangeFilter,
): CalendarDateRangeSelectionResult => {
  const start = appointmentPlanningDateFromValue(value.start)
  const end = appointmentPlanningDateFromValue(value.end)

  if (!start && !end) return { kind: "reset" }

  if (!start || !end) {
    return {
      kind: "invalid",
      message: "Bitte wählen Sie ein Start- und ein Enddatum aus.",
    }
  }

  const range = completeAppointmentPlanningDateRange({ start, end })

  if (!range) {
    return { kind: "invalid", message: "Der gewählte Zeitraum ist ungültig." }
  }

  if (
    inclusiveAppointmentPlanningDayCount(range.start, range.end) >
    APPOINTMENT_PLANNING_CALENDAR_MAX_DAYS
  ) {
    return {
      kind: "invalid",
      message: "Der Kalenderzeitraum darf höchstens 16 Wochen umfassen.",
    }
  }

  return { kind: "valid", range }
}

type PlannedAppointmentRow = {
  status?: string | null
  appointment?: { start_at?: string | null } | null
}

export const buildAutomaticAppointmentPlanningCalendarDateRange = (
  rows: PlannedAppointmentRow[],
): CompleteAppointmentPlanningDateRange | null => {
  const appointmentDays = rows
    .filter((row) => row.status === "planned")
    .map((row) => appointmentPlanningDateFromValue(row.appointment?.start_at))
    .filter((value): value is Date => Boolean(value))
    .map(startOfDay)
    .sort((left, right) => left.getTime() - right.getTime())

  if (!appointmentDays.length) return null

  return {
    start: startOfWeek(appointmentDays[0]),
    end: addDays(startOfWeek(appointmentDays[appointmentDays.length - 1]), 6),
  }
}

export const serializeAppointmentPlanningFilters = (
  filters: AppointmentPlanningFilterState,
): string => {
  const dateRange = normalizeAppointmentPlanningDateRange(filters.dateRange)
  const start = appointmentPlanningDateFromValue(dateRange.start)?.toISOString() ?? ""
  const end = appointmentPlanningDateFromValue(dateRange.end)?.toISOString() ?? ""

  return [
    normalizeAppointmentPlanningSearch(filters.search),
    String(filters.employeeId ?? "").trim(),
    start,
    end,
  ].join("|")
}

export const buildAppointmentPlanningEmployeeFilterOptions = (
  rows: PlanningPreviewRow[],
): PlanningPreviewSelectOption[] => {
  const byId = new Map<string, string>()
  rows.forEach((row) => {
    const value = String(row.appointmentEmployeeId ?? "").trim()

    if (!value || byId.has(value)) return

    const label = String(row.appointmentEmployeeLabel ?? "").trim() || "Mitarbeiter"
    byId.set(value, label)
  })

  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label, "de", { sensitivity: "base" }))
}

export const filterAppointmentPlanningRows = (
  rows: PlanningPreviewRow[],
  filters: AppointmentPlanningFilterState,
): PlanningPreviewRow[] => {
  const search = normalizeAppointmentPlanningSearch(filters.search)
  const employeeId = String(filters.employeeId ?? "").trim()
  const dateRange = normalizeAppointmentPlanningDateRange(filters.dateRange)
  const start = appointmentPlanningDateFromValue(dateRange.start)
  const end = appointmentPlanningDateFromValue(dateRange.end)
  const startTimestamp = start ? startOfDay(start).getTime() : null
  const endTimestamp = end ? endOfDay(end) : null
  const hasDateFilter = startTimestamp !== null || endTimestamp !== null

  return rows.filter((row) => {
    if (search && !normalizeAppointmentPlanningSearch(row.searchText).includes(search)) return false

    if (employeeId && row.appointmentEmployeeId !== employeeId) return false

    if (!hasDateFilter) return true

    const appointmentTimestamp = row.appointmentSortValue

    if (appointmentTimestamp === null || !Number.isFinite(appointmentTimestamp)) return false

    if (startTimestamp !== null && appointmentTimestamp < startTimestamp) return false

    if (endTimestamp !== null && appointmentTimestamp > endTimestamp) return false

    return true
  })
}

const compareNullableAppointment = (
  left: PlanningPreviewRow,
  right: PlanningPreviewRow,
  direction: 1 | -1,
) => {
  const leftValue = left.appointmentSortValue
  const rightValue = right.appointmentSortValue
  const leftMissing = leftValue === null || !Number.isFinite(leftValue)
  const rightMissing = rightValue === null || !Number.isFinite(rightValue)

  if (leftMissing && rightMissing) return 0

  if (leftMissing) return 1

  if (rightMissing) return -1

  if (leftValue === rightValue) return 0

  return leftValue < rightValue ? -1 * direction : direction
}

const rowNumberValue = (row: PlanningPreviewRow): number | null => {
  const value = row[STABLE_ROW_NUMBER_COLUMN_ID]

  return typeof value === "number" && Number.isFinite(value) ? value : null
}

const compareStableRowNumber = (
  left: PlanningPreviewRow,
  right: PlanningPreviewRow,
  direction: 1 | -1,
) => {
  const leftValue = rowNumberValue(left)
  const rightValue = rowNumberValue(right)

  if (leftValue !== null && rightValue !== null && leftValue !== rightValue) {
    return leftValue < rightValue ? -1 * direction : direction
  }

  if (leftValue !== null && rightValue === null) return -1

  if (leftValue === null && rightValue !== null) return 1

  return String(left.id).localeCompare(String(right.id), "de", { sensitivity: "base" })
}

export const sortAppointmentPlanningRows = (
  rows: PlanningPreviewRow[],
  sort: AppointmentPlanningTableSortState,
): PlanningPreviewRow[] => {
  const direction: 1 | -1 = sort?.dir === "desc" ? -1 : 1

  if (sort?.columnId === STABLE_ROW_NUMBER_COLUMN_ID || sort?.key === STABLE_ROW_NUMBER_COLUMN_ID) {
    return [...rows].sort((left, right) => compareStableRowNumber(left, right, direction))
  }

  return [...rows].sort((left, right) => {
    const appointmentComparison = compareNullableAppointment(left, right, direction)

    if (appointmentComparison !== 0) return appointmentComparison

    const employeeComparison = left.appointmentEmployeeLabel.localeCompare(
      right.appointmentEmployeeLabel,
      "de",
      { sensitivity: "base" },
    )

    if (employeeComparison !== 0) return employeeComparison

    const propertyComparison = left.propertyTag.label.localeCompare(right.propertyTag.label, "de", {
      sensitivity: "base",
    })

    if (propertyComparison !== 0) return propertyComparison

    return String(left.id).localeCompare(String(right.id), "de", { sensitivity: "base" })
  })
}

export const getAppointmentPlanningVisibleRows = (
  rows: PlanningPreviewRow[],
  filters: AppointmentPlanningFilterState,
  sort: AppointmentPlanningTableSortState,
): PlanningPreviewRow[] =>
  sortAppointmentPlanningRows(filterAppointmentPlanningRows(rows, filters), sort)
